import { useCallback, useContext, useState, useMemo } from 'react';
import * as R from 'ramda';
import { MdArrowBackIosNew, MdArrowForwardIos, MdDoubleArrow } from 'react-icons/md';
import {
  KpiEdits,
  SourceMapping,
  AccountInfo,
  KpiMappings,
  Status
} from '@blisspointmedia/bpm-types/dist/KpiMapping';
import { formatNumberAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  KpiMappingsContext,
  CombinedCrossChannelKpis,
  HandleKpiEditInput,
  HandleLagEditInput,
  HandleNewRowInput,
  makeEditKey
} from './kpi-mapping-types';
import { Button } from '../../Components/Button';
import { Appearance, Placement, Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { Spinner } from '../../Components/Spinner';

interface SelectOnChangeParams {
  originalValues: { label: string; value: string }[];
  newValues: { label: string; value: string }[];
  sourceKey: string;
  crossChannelKpiId: string | number;
  accountId: string;
  editsKey: string;
  isNewRow?: boolean;
}

interface ApplyKpisToAllAccountsParams {
  valuesToUse: { label: string; value: string }[];
  crossChannelKpiId: string | number;
  crossChannelKpiLabel: string;
  accountIdToCopyFrom: string;
  isNewRow: boolean | undefined;
}

const LAG_OPTIONS: Record<string, { label: string; value: string }[]> = {
  tvad: [
    { label: '1d', value: '1' },
    { label: '3d', value: '3' },
    { label: '7d', value: '7' },
    { label: '14d', value: '14' },
    { label: '30d', value: '30' }
  ],
  criteo: [
    { label: '1d', value: '1' },
    { label: '7d', value: '7' },
    { label: '30d', value: '30' }
  ]
};

const getOriginalValues = ({
  accountId,
  source,
  kpiMappingsKey,
  combinedKpiMappings
}: {
  accountId: string;
  source: string;
  kpiMappingsKey: string | number;
  combinedKpiMappings: KpiMappings;
}) => {
  const sourceMappings: SourceMapping[] =
    combinedKpiMappings[kpiMappingsKey]?.mappings?.[source] || [];

  const mappingsForAccount = sourceMappings.filter(m => m.accountId === accountId);

  return mappingsForAccount.map(mapping => ({ label: mapping.kpi, value: mapping.kpi }));
};

const getValuesToUse = ({
  accountId,
  editsKey,
  editsMap,
  source,
  kpiMappingsKey,
  combinedKpiMappings
}: {
  accountId: string;
  editsKey: string;
  editsMap: KpiEdits;
  source: string;
  kpiMappingsKey: string | number;
  combinedKpiMappings: KpiMappings;
}) => {
  const originalValues = getOriginalValues({
    accountId,
    source,
    kpiMappingsKey,
    combinedKpiMappings
  });

  if (editsKey in editsMap) {
    const edits = editsMap[editsKey] || [];
    const addedValues = edits
      .filter((edit: SourceMapping) => edit.status === Status.INSERT)
      .map((edit: SourceMapping) => ({
        label: edit.kpi,
        value: edit.kpi
      }));

    const deletedValues = edits
      .filter((edit: SourceMapping) => edit.status === Status.DELETE)
      .reduce<Record<string, boolean>>(
        (prev, edit: SourceMapping) => ({ ...prev, [edit.kpi]: true }),
        {}
      );

    return [...originalValues.filter(row => !deletedValues[row.value]), ...addedValues];
  }
  return originalValues;
};

interface SourceColumnProps {
  combinedKpiMappings: KpiMappings;
  source: string;
  crossChannelKpis: CombinedCrossChannelKpis;
  editsMap: KpiEdits;
  newRows: KpiMappings;
  accounts: AccountInfo[];
  lagEdits: Record<string, string>;
  handleKpiEdit: (input: HandleKpiEditInput) => void;
  handleNewRow: (input: HandleNewRowInput) => void;
  handleLagEdit: (input: HandleLagEditInput) => void;
}

const SourceColumn = ({
  combinedKpiMappings,
  source,
  crossChannelKpis,
  editsMap,
  newRows,
  accounts,
  lagEdits,
  handleKpiEdit,
  handleNewRow,
  handleLagEdit
}: SourceColumnProps) => {
  const {
    sourceIdMap,
    kpiOptions,
    editMode,
    sourceToPrettyName,
    rawSourceKpiCounts,
    kpiCountsLoading,
    lags
  } = useContext(KpiMappingsContext);

  const [collapsed, setCollapsed] = useState(false);

  const selectOnChange = useCallback(
    ({
      originalValues,
      newValues,
      sourceKey,
      crossChannelKpiId,
      accountId,
      editsKey,
      isNewRow
    }: SelectOnChangeParams) => {
      const oldVals = originalValues.map(val => val.value);
      const newVals = newValues.map(val => val.value);

      const added = newVals.filter(val => !oldVals.includes(val));
      const removed = oldVals.filter(val => !newVals.includes(val));

      const finalAdded = added.filter(kpi => !removed.includes(kpi));
      const finalRemoved = removed.filter(kpi => !added.includes(kpi));

      const addedMappings: SourceMapping[] = finalAdded.map(kpi => ({
        kpi,
        crossChannelKpiId,
        source: sourceKey,
        sourceId: sourceIdMap[sourceKey],
        accountId,
        status: Status.INSERT
      }));

      const removedMappings: SourceMapping[] = finalRemoved.map(kpi => ({
        kpi,
        crossChannelKpiId,
        source: sourceKey,
        sourceId: sourceIdMap[sourceKey],
        accountId,
        status: Status.DELETE
      }));

      const mappings = [...addedMappings, ...removedMappings];

      if (isNewRow) {
        handleNewRow({
          crossChannelKpiId: crossChannelKpiId as string,
          kpiLabel: '',
          sourceLabel: sourceKey,
          isCrossChannelKpiLabel: false,
          sourceMappings: mappings
        });
      } else {
        handleKpiEdit({
          key: editsKey,
          mappings
        });
      }
    },
    [sourceIdMap, handleKpiEdit, handleNewRow]
  );

  const applyKpisToAllAccounts = useCallback(
    ({
      valuesToUse,
      crossChannelKpiId,
      crossChannelKpiLabel,
      accountIdToCopyFrom,
      isNewRow
    }: ApplyKpisToAllAccountsParams) => {
      if (isNewRow) {
        const newKpis: SourceMapping[] = newRows[crossChannelKpiId]?.mappings?.[source] || [];

        const newKpisForAccount = newKpis.filter(kpi => kpi.accountId === accountIdToCopyFrom);

        accounts.forEach(account => {
          const { accountId } = account;
          const edits = newKpisForAccount.map((edit: SourceMapping) => ({
            ...edit,
            accountId
          }));

          handleNewRow({
            crossChannelKpiId: crossChannelKpiId as string,
            kpiLabel: '',
            sourceLabel: source,
            isCrossChannelKpiLabel: false,
            sourceMappings: edits,
            accountId
          });
        });
      } else {
        const sourceMappings: SourceMapping[] =
          combinedKpiMappings[crossChannelKpiLabel]?.mappings?.[source] || [];

        accounts.forEach(account => {
          const { accountId } = account;
          if (accountId === accountIdToCopyFrom) {
            return;
          }

          const originalValues = sourceMappings
            .filter(m => m.accountId === accountId)
            .map(mapping => ({ label: mapping.kpi, value: mapping.kpi }));

          const editsKey = makeEditKey({
            crossChannelKpiId,
            source,
            accountId
          });

          selectOnChange({
            originalValues,
            newValues: valuesToUse,
            sourceKey: source,
            crossChannelKpiId,
            accountId,
            editsKey
          });
        });
      }
    },
    [source, newRows, accounts, handleNewRow, combinedKpiMappings, selectOnChange]
  );

  const getKpiCounts = useCallback(
    (valuesToUse: { label: string; value: string }[], accountId: string) => {
      if (valuesToUse.length === 0) return 0;

      let total = 0;
      for (const { value } of valuesToUse) {
        const lag = lagEdits[source] || lags[source] || 'default';
        const fallbackCount = R.pathOr(
          0,
          [source, accountId, value, 'default'],
          rawSourceKpiCounts
        );
        const count = R.pathOr(fallbackCount, [source, accountId, value, lag], rawSourceKpiCounts);
        total += count;
      }

      return total;
    },
    [rawSourceKpiCounts, source, lagEdits, lags]
  );

  const lagToUse = useMemo(
    () => lagEdits[source] || lags[source] || 'Not set',
    [lagEdits, lags, source]
  );

  const makeDropdownOptions = useCallback(
    (kpis: string[]) => kpis.map(kpi => ({ label: kpi, value: kpi })),
    []
  );

  return (
    <div className='source-column' data-testid='source-column'>
      <div className='column-header'>
        <div>{sourceToPrettyName[source] || source}</div>
        {LAG_OPTIONS[source] &&
          (editMode ? (
            <div className='lag-dropdown'>
              <Dropdown
                size={Size.LG}
                label='Lag'
                value={lagToUse}
                options={LAG_OPTIONS[source]}
                onChange={lag => handleLagEdit({ source, lag })}
              />
            </div>
          ) : (
            <div className='lag-display'>{`Lag: ${
              lagToUse === 'Not set' ? lagToUse : `${lagToUse}d`
            }`}</div>
          ))}
        {accounts.length > 1 && (
          <Button
            size={Size.SM}
            className='collapse-button'
            appearance={Appearance.EMPTY}
            onClick={() => setCollapsed(prev => !prev)}
            leadingElement={collapsed ? <MdArrowForwardIos /> : <MdArrowBackIosNew />}
          />
        )}
      </div>
      <div className='account-id-columns'>
        {collapsed ? (
          <div className='collapsed-message'>
            Expand to see raw KPIs for all {sourceToPrettyName[source] || source} accounts.
          </div>
        ) : (
          accounts.map((accountInfo, i) => {
            const { accountId, accountName } = accountInfo;
            const dropdownOptions = makeDropdownOptions(
              R.pathOr([] as string[], [source, accountId], kpiOptions)
            );

            return (
              <div className='account-id-column' key={accountId}>
                <div className='account-id-label'>
                  <div className='label-prefix'>Account:</div>
                  <div className='label-value'>{accountName || accountId || 'N/A'}</div>
                </div>
                {crossChannelKpis.map(({ crossChannelKpiLabel, crossChannelKpiId, isNewRow }) => {
                  const kpiMappingsKey = isNewRow ? crossChannelKpiId : crossChannelKpiLabel;

                  const editsKey = makeEditKey({ crossChannelKpiId, source, accountId });

                  const valuesToUse = getValuesToUse({
                    accountId,
                    editsKey,
                    editsMap,
                    source,
                    kpiMappingsKey,
                    combinedKpiMappings
                  });

                  const originalValues = getOriginalValues({
                    accountId,
                    source,
                    kpiMappingsKey,
                    combinedKpiMappings
                  });

                  const kpiCounts = getKpiCounts(valuesToUse, accountId);

                  return (
                    <div key={crossChannelKpiId} className='source-item' data-testid='source-item'>
                      <div className='source-kpi-picker'>
                        {i === 0 && accounts.length > 1 && editMode && (
                          <OverlayTrigger
                            placement={Placement.BOTTOM}
                            delay={500}
                            overlay={
                              <Tooltip>
                                Apply selected KPI(s) to all accounts for{' '}
                                {sourceToPrettyName[source]}
                              </Tooltip>
                            }
                          >
                            <span>
                              <Button
                                size={Size.SM}
                                className='apply-all-button'
                                appearance={Appearance.OUTLINED}
                                onClick={() =>
                                  applyKpisToAllAccounts({
                                    crossChannelKpiId,
                                    crossChannelKpiLabel,
                                    accountIdToCopyFrom: accountId,
                                    isNewRow,
                                    valuesToUse
                                  })
                                }
                              >
                                <MdDoubleArrow />
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                        <div className='source-kpi-dropdown'>
                          <Dropdown
                            size={Size.LG}
                            searchable
                            multiSelect
                            disabled={!editMode}
                            className='kpi-selector-dropdown'
                            placeholder='Choose Raw KPI(s)'
                            clearable
                            options={dropdownOptions}
                            value={valuesToUse.map(v => v.value)}
                            onChange={newVals => {
                              selectOnChange({
                                originalValues,
                                newValues: newVals.map(v => ({ label: v, value: v })),
                                sourceKey: source,
                                crossChannelKpiId,
                                accountId,
                                editsKey,
                                isNewRow
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className='raw-source-kpi-count' data-testid='kpi-volume'>
                        {valuesToUse.length > 0 &&
                          (kpiCountsLoading ? (
                            <Spinner size={14} />
                          ) : (
                            <>
                              <div>KPI Volume:</div>
                              <div>{formatNumberAsInt(kpiCounts)}</div>
                            </>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export { SourceColumn };
